// /app/frontend/src/hooks/useReadReceipts.js
import { useEffect, useState, useCallback } from "react";

const BACKEND_URL = window.location.origin;

/**
 * useReadReceipts
 *
 * - Fetches partner's last read and delivered message from backend (persistent)
 * - Listens for real-time 'chat_read' events via Ably
 * - Sends both real-time + persistent read receipts
 *
 * @param {Object} params
 * @param {Object} params.ably - Ably Realtime client instance
 * @param {string} params.conversationId - Unique conversation/match ID
 * @param {string} params.currentUserId - Current user's ID
 * @param {string} params.token - JWT auth token for backend calls
 * @returns {Object} { partnerLastReadId, partnerLastDeliveredId, sendReadReceipt }
 */
export function useReadReceipts({ ably, conversationId, currentUserId, token }) {
  const [partnerLastReadId, setPartnerLastReadId] = useState(null);
  const [partnerLastDeliveredId, setPartnerLastDeliveredId] = useState(null);

  // 1) Initial fetch from backend (persistent state)
  useEffect(() => {
    if (!conversationId || !token) return;

    let cancelled = false;

    async function fetchInitialReceipts() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/chat/${conversationId}/receipts`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (!res.ok) throw new Error("Failed to fetch receipts");
        const data = await res.json();
        
        if (cancelled) return;

        if (data.partner_last_read_message_id) {
          setPartnerLastReadId(data.partner_last_read_message_id);
        }
        if (data.partner_last_delivered_message_id) {
          setPartnerLastDeliveredId(data.partner_last_delivered_message_id);
        }
      } catch (err) {
        // Silent fail – we still have real-time Ably as a fallback
        console.log('[useReadReceipts] Failed to fetch initial receipts:', err.message);
      }
    }

    fetchInitialReceipts();

    return () => {
      cancelled = true;
    };
  }, [conversationId, token]);

  // 2) Real-time listener via Ably (chat_read events)
  useEffect(() => {
    if (!ably || !conversationId) return;

    const channel = ably.channels.get(`chat:match:${conversationId}`);
    
    // Add safe error handlers to prevent crashes
    const safeErrorHandler = (err) => {
      console.error('[ReadReceipts] Channel error:', err);
      // DO NOT throw
    };
    const safeDetachedHandler = (stateChange) => {
      console.warn('[ReadReceipts] Channel detached:', stateChange?.reason || '');
      // DO NOT throw - normal lifecycle
    };
    
    channel.off('error', safeErrorHandler);
    channel.off('detached', safeDetachedHandler);
    channel.on('error', safeErrorHandler);
    channel.on('detached', safeDetachedHandler);

    const onMessage = (msg) => {
      if (msg.name === "chat_read") {
        const { fromUserId, lastReadMessageId, lastDeliveredMessageId } = msg.data || {};
        if (!fromUserId) return;
        if (fromUserId === currentUserId) return; // ignore our own events

        if (lastReadMessageId) {
          setPartnerLastReadId(lastReadMessageId);
        }
        if (lastDeliveredMessageId) {
          setPartnerLastDeliveredId(lastDeliveredMessageId);
        }
      }
    };

    channel.subscribe(onMessage);
    return () => {
      channel.unsubscribe(onMessage);
      setPartnerLastReadId(null);
      setPartnerLastDeliveredId(null);
    };
  }, [ably, conversationId, currentUserId]);

  // 3) Send read receipt (real-time + persistent)
  const sendReadReceipt = useCallback(
    async (messageId) => {
      if (!conversationId || !currentUserId) return;

      // Real-time Ably event
      if (ably) {
        try {
          const channel = ably.channels.get(`chat:match:${conversationId}`);
          channel.publish("chat_read", {
            type: "chat_read",
            fromUserId: currentUserId,
            lastReadMessageId: messageId,
          });
        } catch (err) {
          // Ignore realtime errors
          console.log('[useReadReceipts] Failed to send real-time read receipt:', err.message);
        }
      }

      // Persistent backend state - using new receipts endpoint
      if (token) {
        try {
          await fetch(
            `${BACKEND_URL}/api/chat/${conversationId}/receipts`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ last_read_message_id: messageId }),
            }
          );
        } catch (err) {
          // Silent – worst case, realtime still works
          console.log('[useReadReceipts] Failed to persist read receipt:', err.message);
        }
      }
    },
    [ably, conversationId, currentUserId, token]
  );

  return {
    partnerLastReadId,
    partnerLastDeliveredId,
    sendReadReceipt,
  };
}
